// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, CircularProgress, Alert, Grid,
  Stack, Divider, Card, CardContent
} from '@mui/material';
import { Add as AddIcon, Receipt as ReceiptIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';

interface Expense {
  id: string;
  expenseNo: string;
  amount: string;
  paymentDate: string;
  status: string;
  description: string;
  category: { name: string };
  account: { name: string, code: string };
  paidFrom: { name: string, code: string };
}

const COLORS = ['#4C51BF', '#48BB78', '#ED8936', '#E53E3E', '#38B2AC', '#9F7AEA'];

const ExpensesPage = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  
  const [categories, setCategories] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>({ totalExpenses: 0, count: 0, categoryBreakdown: [] });

  const [formData, setFormData] = useState({
    categoryId: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    description: '',
    paymentMethod: 'BANK_TRANSFER',
    vendorName: '',
    accountId: '',
    paidFromId: '',
    notes: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const headers = { Authorization: `Bearer ${token}` };
      
      const [expRes, catRes, accRes, sumRes] = await Promise.all([
        axios.get(getApiUrl('expenses'), { headers }),
        axios.get(getApiUrl('expenses/categories'), { headers }),
        axios.get(getApiUrl('accounts'), { headers }),
        axios.get(getApiUrl('expenses/summary'), { headers })
      ]);
      
      setExpenses(expRes.data);
      setCategories(catRes.data);
      setAccounts(accRes.data);
      setSummary(sumRes.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      await axios.post(getApiUrl('expenses'), formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenDialog(false);
      // Reset form
      setFormData({
        categoryId: '', amount: '', paymentDate: new Date().toISOString().split('T')[0], description: '',
        paymentMethod: 'BANK_TRANSFER', vendorName: '', accountId: '', paidFromId: '', notes: ''
      });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to record expense. Double check your ledger mappings.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'PENDING': return 'warning';
      case 'REJECTED': return 'error';
      case 'PAID': return 'info';
      default: return 'default';
    }
  };

  const expenseAccounts = accounts.filter(a => a.type === 'EXPENSE');
  const assetAccounts = accounts.filter(a => ['ASSET', 'BANK', 'CASH'].includes(a.type));

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={900} color="#1A202C">Expense Management</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ borderRadius: '8px', fontWeight: 700, px: 3, py: 1 }}
        >
          Record Expense
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 4, borderRadius: '12px', fontWeight: 600 }}>{error}</Alert>}

      {/* Analytics Dashboard */}
      <Grid container spacing={4} sx={{ mb: 5 }}>
        <Grid item xs={12} md={4}>
          <Grid container spacing={3}>
             <Grid item xs={12}>
                <Card sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none', bgcolor: '#EBF4FF' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                      <TrendingUpIcon sx={{ color: '#4C51BF' }} />
                      <Typography variant="subtitle2" color="#4A5568" fontWeight={700}>TOTAL EXPENSES</Typography>
                    </Stack>
                    <Typography variant="h3" fontWeight={900} color="#1A202C">
                      ${Number(summary.totalExpenses).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                    <Typography variant="body2" color="#4A5568" mt={1}>Across {summary.count} approved transactions</Typography>
                  </CardContent>
                </Card>
             </Grid>
          </Grid>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none', height: 200, display: 'flex' }}>
             <Box sx={{ flex: 1, height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summary.categoryBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="amount"
                      nameKey="category"
                    >
                      {summary.categoryBreakdown.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                    <Legend verticalAlign="middle" align="right" layout="vertical" />
                  </PieChart>
                </ResponsiveContainer>
             </Box>
             {summary.categoryBreakdown.length === 0 && (
                <Box sx={{ position: 'absolute', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">No data available</Typography>
                </Box>
             )}
          </Paper>
        </Grid>
      </Grid>

      {/* Expenses Table */}
      <Paper sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none', overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}>
            <Typography variant="h6" fontWeight={800} color="#2D3748">Recent Transactions</Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Category & Account</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Description</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Amount</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.map((expense) => (
                <TableRow key={expense.id} hover>
                  <TableCell sx={{ fontWeight: 600, color: '#4A5568' }}>{expense.expenseNo}</TableCell>
                  <TableCell>{new Date(expense.paymentDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                      <Typography variant="body2" fontWeight={700}>{expense.category?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">Ledger: {expense.account?.code} {expense.account?.name}</Typography>
                  </TableCell>
                  <TableCell>
                      <Typography variant="body2">{expense.description}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Paid via: {expense.paidFrom?.name}
                      </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>${Number(expense.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={expense.status} 
                      color={getStatusColor(expense.status) as any} 
                      size="small"
                      sx={{ fontWeight: 700, borderRadius: '6px' }} 
                    />
                  </TableCell>
                </TableRow>
              ))}
              {expenses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <ReceiptIcon sx={{ fontSize: 48, color: '#CBD5E0', mb: 2 }} />
                    <Typography color="text.secondary" fontWeight={600}>No expenses recorded yet</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Record Expense Dialog (Double Entry Driven) */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' }}}>
        <DialogTitle sx={{ p: 3, pb: 2, fontWeight: 800 }}>Record an Expense</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ px: 4, py: 2 }}>
            <Alert severity="info" sx={{ mb: 3, borderRadius: '8px' }}>
                This will automatically post a double-entry journal hitting the accounts selected below.
            </Alert>
            
            <Typography variant="subtitle2" fontWeight={800} color="#4A5568" mb={2}>1. EXPENSE DETAILS</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth label="Amount" type="number" inputProps={{ step: "0.01", min: "0" }}
                  value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select fullWidth label="Expense Category"
                  value={formData.categoryId} onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })} required
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth label="Payment Date" type="date" InputLabelProps={{ shrink: true }}
                  value={formData.paymentDate} onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })} required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Vendor / Payee Name (Optional)"
                  value={formData.vendorName} onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Internal Description"
                  value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="subtitle2" fontWeight={800} color="#4A5568" mb={2}>2. LEDGER ACCOUNTING (DOUBLE ENTRY)</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth label="Debit (Expense Account)" helperText="The ledger account incurring the cost"
                  value={formData.accountId} onChange={(e) => setFormData({ ...formData, accountId: e.target.value })} required
                >
                  {expenseAccounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>{acc.code} - {acc.name}</MenuItem>
                  ))}
                  {expenseAccounts.length === 0 && <MenuItem disabled>No Expense accounts configured</MenuItem>}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth label="Credit (Payment Source Asset)" helperText="The bank/asset account paying for it"
                  value={formData.paidFromId} onChange={(e) => setFormData({ ...formData, paidFromId: e.target.value })} required
                >
                  {assetAccounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>{acc.code} - {acc.name} (${Number(acc.balance).toFixed(2)})</MenuItem>
                  ))}
                  {assetAccounts.length === 0 && <MenuItem disabled>No Asset/Bank accounts configured</MenuItem>}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth label="Payment Method"
                  value={formData.paymentMethod} onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })} required
                >
                  <MenuItem value="CASH">Cash</MenuItem>
                  <MenuItem value="BANK_TRANSFER">Bank Transfer (EFT)</MenuItem>
                  <MenuItem value="CHECK">Check</MenuItem>
                  <MenuItem value="CARD">Credit/Debit Card</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Receipt Reference / Notes (Optional)"
                  value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={() => setOpenDialog(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
            <Button type="submit" variant="contained" sx={{ borderRadius: '8px', fontWeight: 700 }}>
              Record & Post Ledger
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ExpensesPage;
